import React, { useState, useEffect } from 'react';
import Tippy from '@tippyjs/react';
import KpiCardGrid from './KpiCardGrid';
import DateModeToggle from './DateModeToggle';
import { getActivePreset } from './DateRangePicker';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import TableSettings from '../../shared/components/TableSettings';
import TeamPagination from '../../TeamManager/components/TeamPagination';
import {
  NEXT_EVENTS_COLUMNS,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
} from '../constants/nextEventsColumnsConfig';
import { currencyFormatter, compactCurrency, compactCurrencyAxis } from '../utils/reportingUtils';

const VENUE_COLORS = ['var(--sl-fg-brand, #1982C4)', '#FAB714', '#8AC926', '#6A4C93', '#2CA8A0', '#F47C2C', 'var(--sl-text-quaternary, #6B7280)', '#E86A92'];

const formatEventDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const RevenueTrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{ background: '#000', borderRadius: 8, padding: 12, minWidth: 140 }}>
      <p style={{ color: 'var(--sl-text-white, #fff)', fontSize: 12, margin: '0 0 8px 0' }}>{label}</p>
      {payload.map((entry, idx) => (
        <div key={idx} className="d-flex align-items-center" style={{ gap: 6, marginBottom: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: entry.color, flexShrink: 0 }} />
          <span style={{ color: '#a1a1aa', fontSize: 12 }}>{entry.name}:</span>
          <span style={{ color: 'var(--sl-text-white, #fff)', fontSize: 12, fontWeight: 600 }}>{currencyFormatter.format(entry.value)}</span>
        </div>
      ))}
    </div>
  );
};

const CURRENT_PERIOD_LABELS = {
  today: 'Today',
  yesterday: 'Yesterday',
  this_week: 'This Week',
  last_week: 'Last Week',
  this_month: 'This Month',
  last_month: 'Last Month',
  month_to_date: 'Month to Date',
  this_year: 'This Year',
  last_year: 'Last Year',
  year_to_date: 'Year to Date',
  all_time: 'All Time',
};

const PRIOR_PERIOD_LABELS = {
  today: 'Yesterday',
  yesterday: 'Day Before',
  this_week: 'Last Week',
  last_week: 'Prior Week',
  this_month: 'Last Month',
  last_month: 'Prior Month',
  month_to_date: 'Last Month to Date',
  this_year: 'Last Year',
  last_year: 'Prior Year',
  year_to_date: 'Last Year to Date',
  all_time: 'Prior Period',
};

const CardTitleLink = ({ title, href, style, tooltip }) => (
  <h6 style={{ fontWeight: 700, fontSize: 16, color: 'var(--sl-text-primary, #101828)', display: 'inline-flex', alignItems: 'center', gap: 8, ...style }}>
    {href ? (
      <a href={href} style={{ color: 'inherit', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {title}
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
          <path d="M3 8h10M9 4l4 4-4 4" stroke="#667085" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </a>
    ) : title}
    {tooltip && (
      <Tippy content={tooltip} placement="top" arrow={false} theme="reporting">
        <span style={{ cursor: 'help', color: 'var(--sl-text-disabled, #98a2b3)', fontSize: 14, lineHeight: 1, fontWeight: 400 }}>
          <i className="far fa-info-circle" />
        </span>
      </Tippy>
    )}
  </h6>
);

const RevenueTrendChart = ({ data, loading, comparisonLabel, dateRange, teamId }) => {
  const months = data ? data.months : [];
  const totalCurrent = data ? data.total_current : 0;
  const totalPrior = data ? data.total_prior : 0;
  const pctChange = totalPrior > 0 ? ((totalCurrent - totalPrior) / totalPrior * 100).toFixed(1) : null;
  const isPositive = pctChange != null && parseFloat(pctChange) >= 0;

  const activePreset = dateRange ? getActivePreset(dateRange) : null;
  const currentLabel = (activePreset && CURRENT_PERIOD_LABELS[activePreset]) || 'Current Period';
  const priorLabel = (activePreset && PRIOR_PERIOD_LABELS[activePreset]) || 'Prior Period';

  return (
    <div style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, padding: 24, flex: 2, minWidth: 0 }}>
      <CardTitleLink
        title="Revenue Trend"
        href={teamId ? `/teams/${teamId}/reporting/dashboard/sales` : null}
        style={{ margin: '0 0 4px 0' }}
        tooltip="Net to organizer — ticket face value plus your service fees. Excludes Opendate and processing fees and donations."
      />
      <div className="d-flex align-items-center" style={{ gap: 8, marginBottom: 16 }}>
        <span style={{ fontSize: 24, fontWeight: 700, color: 'var(--sl-text-primary, #101828)' }}>{compactCurrency(totalCurrent)}</span>
        {pctChange && (
          <span className="d-flex align-items-center" style={{ gap: 2, color: isPositive ? '#8AC926' : '#FF595E', fontSize: 13, fontWeight: 600 }}>
            <i className={`fas fa-arrow-${isPositive ? 'up' : 'down'}`} style={{ fontSize: 10 }} />
            {isPositive ? pctChange : pctChange.replace('-', '')}%
          </span>
        )}
        {comparisonLabel && <span style={{ color: 'var(--sl-text-tertiary, #667085)', fontSize: 13 }}>{comparisonLabel}</span>}
      </div>
      {loading ? (
        <div style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--sl-text-disabled, #98a2b3)' }}>
          Loading...
        </div>
      ) : months.length === 0 ? (
        <div style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--sl-text-disabled, #98a2b3)' }}>
          No data available for the selected period.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          {months.length <= 1 ? (
            <BarChart data={months} margin={{ top: 5, right: 20, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'var(--sl-text-tertiary, #667085)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: 'var(--sl-text-tertiary, #667085)' }} axisLine={false} tickLine={false} tickFormatter={compactCurrencyAxis} />
              <Tooltip content={<RevenueTrendTooltip />} cursor={false} />
              <Legend verticalAlign="top" align="right" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingBottom: 8 }} />
              <Bar dataKey="thisYear" name={currentLabel} fill="#1982C4" barSize={40} radius={[4, 4, 0, 0]} />
              <Bar dataKey="lastYear" name={priorLabel} fill="#CBD5E1" barSize={40} radius={[4, 4, 0, 0]} />
            </BarChart>
          ) : (
            <LineChart data={months} margin={{ top: 5, right: 20, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'var(--sl-text-tertiary, #667085)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: 'var(--sl-text-tertiary, #667085)' }} axisLine={false} tickLine={false} tickFormatter={compactCurrencyAxis} />
              <Tooltip content={<RevenueTrendTooltip />} cursor={false} />
              <Legend verticalAlign="top" align="right" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingBottom: 8 }} />
              <Line type="monotone" dataKey="thisYear" name={currentLabel} stroke="#1982C4" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="lastYear" name={priorLabel} stroke="#CBD5E1" strokeWidth={2} dot={false} />
            </LineChart>
          )}
        </ResponsiveContainer>
      )}
    </div>
  );
};

const PRESET_LABELS = {
  today: 'today',
  yesterday: 'yesterday',
  this_week: 'this week',
  last_week: 'last week',
  this_month: 'this month',
  last_month: 'last month',
  month_to_date: 'month to date',
  this_year: 'this year',
  last_year: 'last year',
  year_to_date: 'year to date',
  all_time: 'all time',
};

const EventsByVenueChart = ({ data, loading, dateRange, teamId }) => {
  const chartData = (data || []).map((item, idx) => ({
    ...item,
    color: VENUE_COLORS[idx % VENUE_COLORS.length],
  }));
  const total = chartData.reduce((sum, d) => sum + d.value, 0);

  return (
    <div style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, padding: 24, flex: 1, minWidth: 0 }}>
      <CardTitleLink title="Events by Venue" href={teamId ? `/teams/${teamId}/reporting/dashboard/events` : null} style={{ margin: '0 0 16px 0' }} />
      {loading ? (
        <div style={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--sl-text-disabled, #98a2b3)' }}>
          Loading...
        </div>
      ) : chartData.length === 0 ? (
        <div style={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--sl-text-disabled, #98a2b3)' }}>
          No events for the selected period.
        </div>
      ) : (
        <>
          <div className="d-flex align-items-center" style={{ gap: 24 }}>
            <div style={{ width: 180, height: 180, flexShrink: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    dataKey="value"
                    strokeWidth={0}
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div style={{ flex: 1 }}>
              {chartData.map((venue) => (
                <div key={venue.name} style={{ marginBottom: 10 }}>
                  <div className="d-flex align-items-center" style={{ gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: venue.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: 'var(--sl-text-secondary, #344054)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{venue.name}</span>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--sl-text-tertiary, #667085)', fontWeight: 400, paddingLeft: 16, marginTop: 2 }}>
                    {venue.value} event{venue.value !== 1 ? 's' : ''} ({venue.percent}%)
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--sl-bg-tertiary, #f2f4f7)' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--sl-text-primary, #101828)', lineHeight: 1.2 }}>{total}</div>
            <div style={{ fontSize: 13, color: 'var(--sl-text-tertiary, #667085)' }}>
              total events{dateRange ? ` ${PRESET_LABELS[getActivePreset(dateRange)] || ''}` : ''}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const thStyle = { fontWeight: 500, fontSize: 12, color: 'var(--sl-text-tertiary, #667085)', textTransform: 'uppercase' };

const STORAGE_KEY = 'reportingNextEventsColumns_v2';
const PER_PAGE = 25;

const COLUMN_RENDERERS = {
  event: (event) => (
    <td key="event" style={{ paddingLeft: 24 }}>
      <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--sl-text-primary, #101828)' }}>{event.title}</div>
      {event.promoter_name && <div style={{ fontSize: 12, color: 'var(--sl-text-tertiary, #667085)' }}>{event.promoter_name}</div>}
    </td>
  ),
  date: (event) => (
    <td key="date" style={{ fontSize: 14, color: 'var(--sl-text-secondary, #344054)', whiteSpace: 'nowrap' }}>{formatEventDate(event.event_date)}</td>
  ),
  venue: (event) => (
    <td key="venue" style={{ fontSize: 14, color: 'var(--sl-text-secondary, #344054)' }}>{event.venue_name || '—'}</td>
  ),
  days_until: (event) => (
    <td key="days_until" style={{ fontSize: 14, color: 'var(--sl-text-secondary, #344054)', textAlign: 'right' }}>
      {event.days_until != null ? (
        <span style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: 12,
          fontSize: 12,
          fontWeight: 600,
          background: event.days_until <= 7 ? 'var(--sl-bg-error-primary, #FEF3F2)' : event.days_until <= 30 ? 'var(--sl-bg-warning-primary, #FFFAEB)' : 'var(--sl-bg-success-primary, #F0FDF4)',
          color: event.days_until <= 7 ? 'var(--sl-text-error, #B42318)' : event.days_until <= 30 ? 'var(--sl-text-warning, #B54708)' : 'var(--sl-text-success, #027A48)',
        }}>
          {event.days_until === 0 ? 'Today' : event.days_until === 1 ? 'Tomorrow' : `${event.days_until}d`}
        </span>
      ) : '—'}
    </td>
  ),
  tickets_sold: (event) => (
    <td key="tickets_sold" style={{ fontSize: 14, color: 'var(--sl-text-secondary, #344054)', textAlign: 'right' }}>{(event.total_tickets_sold || 0).toLocaleString()}</td>
  ),
  sold_today: (event) => (
    <td key="sold_today" style={{ fontSize: 14, color: 'var(--sl-text-secondary, #344054)', textAlign: 'right' }}>{(event.tickets_sold_today || 0).toLocaleString()}</td>
  ),
  sold_last_7: (event) => (
    <td key="sold_last_7" style={{ fontSize: 14, color: 'var(--sl-text-secondary, #344054)', textAlign: 'right' }}>{(event.tickets_sold_last_7 || 0).toLocaleString()}</td>
  ),
  sold_last_14: (event) => (
    <td key="sold_last_14" style={{ fontSize: 14, color: 'var(--sl-text-secondary, #344054)', textAlign: 'right' }}>{(event.tickets_sold_last_14 || 0).toLocaleString()}</td>
  ),
  pct_to_capacity: (event) => {
    const sold = event.total_tickets_sold || 0;
    const capacity = event.total_capacity || 0;
    const pct = capacity > 0 ? Math.round((sold / capacity) * 100) : null;
    return (
      <td key="pct_to_capacity" style={{ fontSize: 14, color: 'var(--sl-text-secondary, #344054)', textAlign: 'right' }}>
        {pct != null ? (
          <div className="d-flex align-items-center justify-content-end" style={{ gap: 8 }}>
            <div style={{ width: 60, height: 6, borderRadius: 3, background: 'var(--sl-bg-tertiary, #F2F4F7)', overflow: 'hidden' }}>
              <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', borderRadius: 3, background: pct >= 80 ? '#8AC926' : pct >= 50 ? 'var(--sl-text-warning, #F79009)' : 'var(--sl-fg-brand, #1982c4)' }} />
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, minWidth: 32 }}>{pct}%</span>
          </div>
        ) : '—'}
      </td>
    );
  },
  forecast: (event) => (
    <td key="forecast" style={{ fontSize: 14, color: 'var(--sl-text-secondary, #344054)', textAlign: 'right' }}>{event.forecast != null ? event.forecast.toLocaleString() : '—'}</td>
  ),
  expected_finish: (event) => (
    <td key="expected_finish" style={{ fontSize: 14, color: 'var(--sl-text-secondary, #344054)', textAlign: 'right' }}>{event.expected_finish != null ? event.expected_finish.toLocaleString() : '—'}</td>
  ),
  presenter: (event) => (
    <td key="presenter" style={{ fontSize: 14, color: 'var(--sl-text-secondary, #344054)' }}>{event.presenter || '—'}</td>
  ),
  sold_yesterday: (event) => (
    <td key="sold_yesterday" style={{ fontSize: 14, color: 'var(--sl-text-secondary, #344054)', textAlign: 'right' }}>{(event.tickets_sold_yesterday || 0).toLocaleString()}</td>
  ),
};

const RIGHT_ALIGNED_COLUMNS = ['days_until', 'tickets_sold', 'sold_today', 'sold_last_7', 'sold_last_14', 'sold_yesterday', 'pct_to_capacity', 'forecast', 'expected_finish'];

const UpcomingEventsTable = ({ events, loading, total, teamId }) => {
  const truncated = total > events.length;
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [tableSettingsOpen, setTableSettingsOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(getDefaultVisibleColumns);
  const [hiddenColumns, setHiddenColumns] = useState(getDefaultHiddenColumns);

  useEffect(() => {
    const storageKey = teamId ? `${STORAGE_KEY}_${teamId}` : STORAGE_KEY;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const { visible, hidden } = JSON.parse(saved);
        if (visible && hidden) {
          const lookup = (id) => NEXT_EVENTS_COLUMNS.find((c) => c.id === id);
          const freshVisible = visible.map((v) => lookup(v.id)).filter(Boolean);
          const freshHidden = hidden.map((h) => lookup(h.id)).filter(Boolean);
          const knownIds = new Set([...freshVisible, ...freshHidden].map((c) => c.id));
          const missing = NEXT_EVENTS_COLUMNS.filter((c) => !knownIds.has(c.id));
          setVisibleColumns(freshVisible);
          setHiddenColumns([...freshHidden, ...missing]);
        }
      }
    } catch (_e) {}
  }, [teamId]);

  const handleColumnsChange = (newVisible, newHidden) => {
    setVisibleColumns(newVisible);
    setHiddenColumns(newHidden);
    const storageKey = teamId ? `${STORAGE_KEY}_${teamId}` : STORAGE_KEY;
    localStorage.setItem(storageKey, JSON.stringify({ visible: newVisible, hidden: newHidden }));
  };

  const filtered = (events || []).filter((e) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (e.title || '').toLowerCase().includes(q) ||
      (e.venue_name || '').toLowerCase().includes(q) ||
      (e.promoter_name || '').toLowerCase().includes(q);
  });

  const totalEntries = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalEntries / PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * PER_PAGE;
  const paginated = filtered.slice(pageStart, pageStart + PER_PAGE);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const visibleIds = visibleColumns.map((c) => c.id);
  const colCount = visibleIds.length;

  return (
  <div style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, marginTop: 16 }}>
    <div className="d-flex align-items-center justify-content-between" style={{ padding: '16px 24px', borderBottom: '1px solid var(--sl-bg-tertiary, #f2f4f7)' }}>
      <CardTitleLink title="Upcoming Events" href={teamId ? `/teams/${teamId}/reporting/dashboard/events` : null} style={{ margin: 0 }} />
      <div className="d-flex align-items-center" style={{ gap: 8 }}>
        <button
          className="btn btn-sm d-flex align-items-center"
          title={truncated ? `Export limited to first ${events.length.toLocaleString()} of ${total.toLocaleString()} events` : ''}
          style={{ gap: 6, fontSize: 13, fontWeight: 500, padding: '4px 12px', borderRadius: 6, border: '1px solid var(--sl-border-primary, #d0d5dd)', background: 'var(--sl-bg-primary, #fff)', color: 'var(--sl-text-secondary, #344054)', height: 32 }}
          onClick={() => {
            const header = visibleColumns.map((c) => c.label);
            const rows = filtered.map((e) => visibleIds.map((id) => {
              if (id === 'event') return e.title || '';
              if (id === 'date') return e.event_date ? new Date(e.event_date).toLocaleDateString() : '';
              if (id === 'venue') return e.venue_name || '';
              if (id === 'days_until') return e.days_until != null ? e.days_until : '';
              if (id === 'tickets_sold') return e.total_tickets_sold || 0;
              if (id === 'sold_today') return e.tickets_sold_today || 0;
              if (id === 'sold_last_7') return e.tickets_sold_last_7 || 0;
              if (id === 'sold_last_14') return e.tickets_sold_last_14 || 0;
              if (id === 'pct_to_capacity') return e.total_capacity > 0 ? `${Math.round(((e.total_tickets_sold || 0) / e.total_capacity) * 100)}%` : '';
              if (id === 'forecast') return e.forecast != null ? e.forecast : '';
              if (id === 'expected_finish') return e.expected_finish != null ? e.expected_finish : '';
              if (id === 'presenter') return e.presenter || '';
              if (id === 'sold_yesterday') return e.tickets_sold_yesterday || 0;
              return '';
            }));
            const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'upcoming_events.csv';
            a.click();
            URL.revokeObjectURL(url);
          }}
        >
          Export CSV <i className="fas fa-download" style={{ fontSize: 12 }} />
        </button>
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            className="btn btn-sm d-flex align-items-center"
            onClick={() => setTableSettingsOpen(!tableSettingsOpen)}
            style={{ fontSize: 13, fontWeight: 500, padding: '4px 12px', borderRadius: 6, border: '1px solid var(--sl-border-primary, #d0d5dd)', background: 'var(--sl-bg-primary, #fff)', color: 'var(--sl-text-secondary, #344054)', height: 32 }}
          >
            <i className="fas fa-columns mr-2" style={{ fontSize: 12 }} />
            Table Settings
            <span className="ml-1" style={{ background: 'var(--sl-fg-brand, #1982c4)', color: 'var(--sl-text-white, #fff)', borderRadius: 10, padding: '0 6px', fontSize: 11, fontWeight: 600, lineHeight: '18px' }}>
                {visibleColumns.length}
              </span>
          </button>
          <TableSettings
            isOpen={tableSettingsOpen}
            onClose={() => setTableSettingsOpen(false)}
            visibleFields={visibleColumns}
            hiddenFields={hiddenColumns}
            onFieldsChange={handleColumnsChange}
          />
        </div>
      </div>
    </div>
    <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--sl-bg-tertiary, #f2f4f7)' }}>
      <div style={{ position: 'relative', maxWidth: 400 }}>
        <i className="fas fa-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--sl-text-disabled, #98a2b3)', fontSize: 14 }} />
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Search Events..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ paddingLeft: 36, border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, height: 40 }}
        />
      </div>
      {truncated && (
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--sl-text-warning, #B54708)', background: 'var(--sl-bg-warning-primary, #FFFAEB)', border: '1px solid #FEDF89', borderRadius: 6, padding: '6px 10px' }}>
          <i className="fas fa-info-circle" style={{ marginRight: 6 }} />
          Showing first {events.length.toLocaleString()} of {total.toLocaleString()} upcoming events. Search and filters apply only to the loaded events.
        </div>
      )}
    </div>
    <div className="table-responsive">
      <table className="table table-hover align-middle mb-0">
        <thead>
          <tr>
            {visibleColumns.map((col) => (
              <th key={col.id} style={{ ...thStyle, ...(col.id === 'event' ? { paddingLeft: 24 } : {}), ...(RIGHT_ALIGNED_COLUMNS.includes(col.id) ? { textAlign: 'right' } : {}) }}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={colCount} className="text-center py-4 text-muted">Loading...</td>
            </tr>
          ) : filtered.length === 0 ? (
            <tr>
              <td colSpan={colCount} className="text-center py-4 text-muted">{search ? 'No events match your search.' : 'No upcoming events.'}</td>
            </tr>
          ) : paginated.map((event) => (
            <tr key={event.id}>
              {visibleIds.map((id) => COLUMN_RENDERERS[id] ? COLUMN_RENDERERS[id](event) : null)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {!loading && totalEntries > 0 && (
      <div style={{ padding: '12px 24px', borderTop: '1px solid var(--sl-bg-tertiary, #f2f4f7)' }}>
        <TeamPagination
          currentPage={safePage}
          totalPages={totalPages}
          totalItems={totalEntries}
          pageSize={PER_PAGE}
          itemLabel="events"
          onPageChange={(page) => {
            if (page >= 1 && page <= totalPages) {
              setCurrentPage(page);
            }
          }}
        />
      </div>
    )}
  </div>
  );
};

const RevenueAndChannelSection = ({ revenueTrend, revenueTrendLoading, comparisonLabel, eventsByVenue, eventsByVenueLoading, upcomingEvents, upcomingEventsLoading, upcomingEventsTotal, bookingKpiCards, bookingKpisLoading, bookingDateMode, setBookingDateMode, teamId, isMobile, dateRange }) => (
  <div style={{ marginBottom: 24 }}>
    <div className="d-flex" style={{ gap: 16, flexDirection: isMobile ? 'column' : 'row' }}>
      <RevenueTrendChart data={revenueTrend} loading={revenueTrendLoading} comparisonLabel={comparisonLabel} dateRange={dateRange} teamId={teamId} />
      <EventsByVenueChart data={eventsByVenue} loading={eventsByVenueLoading} dateRange={dateRange} teamId={teamId} />
    </div>
    {bookingKpiCards && (
      <div style={{ marginTop: 24 }}>
        <div className="d-flex align-items-center" style={{ gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 13, color: 'var(--sl-text-tertiary, #667085)', fontWeight: 500, whiteSpace: 'nowrap' }}>Booking activity by:</span>
          <DateModeToggle value={bookingDateMode} onChange={setBookingDateMode} />
        </div>
        <KpiCardGrid loading={bookingKpisLoading} isMobile={isMobile} columns={3} cards={bookingKpiCards} comparisonLabel={comparisonLabel} />
      </div>
    )}
    <UpcomingEventsTable events={upcomingEvents || []} loading={upcomingEventsLoading} total={upcomingEventsTotal || 0} teamId={teamId} />
  </div>
);

export default RevenueAndChannelSection;
